import React, { useEffect, useMemo, forwardRef, useCallback } from 'react';
import { Button, Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';

const initialFormValues = [{
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ project: undefined }]
}];

const DELIMITER = ":";

const FortifyMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
  const projectsRef = useRef({});
  const releaseRef = useRef({});
  const releaseApiRef = useRef([]);
  const [form] = Form.useForm();
  const cancelRef = useRef([]);
  const [mappedProjectReleaseList] = useState(() => {
    // this state contains the already mapped projects/release
    // This is stored because the project and release dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { applicationId, applicationName } = props.record;
      const applicationIdArray = _.split(applicationId, ",");
      const applicationNameArray = _.split(applicationName, ",");

      const projects = [], release = {}
      _.forEach(applicationIdArray, (id, index) => {
        projects.push({ id, name: applicationNameArray[index] });
        projectsRef.current = { ...projectsRef.current, [id]: applicationNameArray[index] }
      })

      return {
        projects: _.uniqBy(projects, "id"),
        release
      }
    }
    return { projects: [], release: {} }
  })

  useEffect(() => {
    // cancelling all release APIs on component unmount
    const cancels = cancelRef.current;
    return () => _.forEach(cancels, cancel => cancel());
  }, []);

  const fetchRelease = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped release to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if (mappedProjectReleaseList.release[params.project] && !_.isEmpty(response.projects)) {
          response.projects = [...response.projects, mappedProjectReleaseList.release[params.project]];
        }
        _.forEach(response.projects, p => {
          releaseRef.current[p.id] = p.name;
        });
        // merging the already mapped release to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if (mappedProjectReleaseList.release[params.project] && !_.isEmpty(response.projects)) {
          response.projects = [...response.projects, mappedProjectReleaseList.release[params.project]];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName, mappedProjectReleaseList]);

  const fetchProjects = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped release to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if (mappedProjectReleaseList.projects.length > 0 && !_.isEmpty(response.projects)) {
          response.projects = [...response.projects, ...mappedProjectReleaseList.projects];
        }
        _.forEach(response.projects, p => {
          projectsRef.current[p.id] = p.name;
        });
        // merging the already mapped release to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if (mappedProjectReleaseList.projects.length > 0 && !_.isEmpty(response.projects)) {
          response.projects = [...response.projects, ...mappedProjectReleaseList.projects];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName, mappedProjectReleaseList]);

  const getExtraFieldsData = useCallback((config) => {
    const { projectList } = config;
    const projectReleaseNameList = [];
    const projectReleaseIDList = []
    const applicationId = _.map(projectList, ({ project }) => project)

    const applicationName = _.map(projectList, ({ project, release }) => {
      if (!_.isEmpty(release)) {
        const projectName = projectsRef.current[project];
        const releaseName = _.map(release, releaseId => releaseRef.current[releaseId])
        projectReleaseIDList.push(project + DELIMITER + release.join(';'));
        projectReleaseNameList.push(projectName + DELIMITER + releaseName.join(';'))
      }
      return projectsRef.current[project];
    })
    return ({
      applicationId: applicationId.join(','),
      releaseId: projectReleaseIDList.join(','),
      releaseName: projectReleaseNameList.join(','),
      applicationName: applicationName.join(','),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { applicationId, releaseId } = config;
    const applicationIdArray = _.split(applicationId, ',')
    const releaseObj = {}
    if (releaseId) {
      const releaseIdArray = _.split(releaseId, ',')
      _.forEach(releaseIdArray, release => {
        let [_projectID, _releaseID] = _.split(release, DELIMITER)
        _releaseID = _.split(_releaseID.replace(/;/g, ','), ',');
        releaseObj[_projectID] = _releaseID
      })
    }
    const projects = _.map(applicationIdArray, id => {
      return { project: id, release: releaseObj[id] }
    })
    return ({
      projectList: projects,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, []);

  const onValuesChange = useCallback((values, ...args) => {
    if (values.configurations) {
      let isProjectChanged = false;
      const formValues = form.getFieldsValue();
      _.forEach(values.configurations, (configs, index) => {

        // If project list is greater than 1, means that a new blank row is added
        // no need to clear the release fields
        if (!configs || !configs.projectList || configs.projectList.length > 1) {
          return;
        }

        // since this is a list field, onchange will trigger only for the changed row.
        // if change is done at 2nd row, data received in this callback will only contain 2nd row data
        // configs.projectList is given as string when we search in the field
        if (!configs || _.isString(configs.projectList)) {
          return;
        }
        _.forEach(configs.projectList, (object, i) => {

          // since this is a list field, onchange will trigger only for the changed row.
          // if change is done at 2nd row, data received in this callback will only contain 2nd row data
          // Also checking if the object contains `project` and nothing else 
          // because we need to reset the release field only if the project is changed
          // and this change is also triggered when rows are added or deleted, so we need to ignore it
          if (object && object.project && Object.keys(object).length === 1) {
            isProjectChanged = true;
            formValues.configurations[index].projectList[i].release = undefined;

            // cache the selected projectName permanently once selected so that even if project page is not 0th it's name is displayed instead of Id
            const selectedProjectId = object.project;
            const selectedProjectName = projectsRef.current[selectedProjectId];

            if (selectedProjectId && selectedProjectName) {
              mappedProjectReleaseList.projects = _.uniqBy(
                [...mappedProjectReleaseList.projects, { id: selectedProjectId, name: selectedProjectName }],
                'id'
              );
            }

          }
        })
      });
      if (isProjectChanged) {
        form.setFieldsValue(formValues);
      }
    }
  }, [form, mappedProjectReleaseList]);

  const isConfigSsc = useMemo(() => {
    let isSscConfig = false;
    isSscConfig = props.loginConfig?.isSSC === "true" ? true : props.record?.isSSC;
    return isSscConfig;
  }, [props.loginConfig?.isSSC, props.record?.isSSC]);

  const projectListRender = useCallback((listField) => {

    // This is a hack, we are storing the api method for release in a ref
    // as it is dependant on `listField` param of this method
    // that is why we cannot move it to `useCallback`
    // For a `listField.name` we are defining the method only once and using the method if it already exists in the ref
    // Reason for doing this is that the method reference will always change and will trigger API on every render
    // It also causes a visible bug on UI as when the API triggers, the selected value disappears as we don't show value when data is loading
    let releaseAPI = releaseApiRef.current[listField.name];
    if (!releaseAPI) {
      releaseAPI = releaseApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
        const allConfigs = form.getFieldsValue().configurations;
        const project = allConfigs[listField.name]?.projectList[nestedListFieldName]?.project;
        if (!project) {
          return { promise: Promise.resolve([]), cancel: _.noop };
        }
        return fetchRelease({ ...params, project });
      }
    }
    // 

    const __fields = [{
      name: 'project',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 9,
      // loading: _.isUndefined(projects),
      props: {
        showValueOnload: false,
        autoFocus: !disableProjectFields,
        optionsMutator: (projects) => {
          const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.project));
          return _.map(projects, p => ({ ...p, disabled: _.includes(selected, p.id) }));
        },
        api: fetchProjects
      },
      required: true
    }, ...(!isConfigSsc ? [{
      name: 'release',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 12,
      props: {
        placeholder: "Leave blank to fetch from latest release",
        api: releaseAPI,
        mode: "multiple",
        setExtraProps: (field, nestedListField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const project = allConfigs[listField.name]?.projectList[nestedListField.name]?.project;
          return {
            showValueOnload: false,
            resetOptionsProp: project
          }
        }
      },
    }, {
      name: "add",
      colSpan: 2,
      render: ({ remove, name, fields }) => {
        return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
      }
    }] : [])]

    return (
      <div className='m-b-sm'>
        <Form.List name={[listField.name, "projectList"]}>
          {(fields, { add, remove }) => {
            return (
              <div>
                {_.map(fields, field => {
                  return generateFormFields(__fields, 9, { ...field, add, remove, fields }, field.name);
                })}
                {!isConfigSsc && <Button size='small' type='dashed' onClick={() => add({ project: undefined })} disabled={disableProjectFields}>+ Add Project</Button>}
              </div>
            )
          }}
        </Form.List>
      </div>
    )
  }, [disableProjectFields, form, fetchProjects, fetchRelease, isConfigSsc]);

  const _fields = useMemo(() => [{
    name: "label",
    type: FieldTypes.LABEL,
    value: <b className='required-mark'>Fortify Project</b>,
    style: { marginBottom: -24 },
    colSpan: 9
  }, ...(!isConfigSsc ? [{
    name: "label",
    type: FieldTypes.LABEL,
    value: <b>Release</b>,
    style: { marginBottom: -24 },
    colSpan: 9
  }] : []), {
    name: (listField) => [listField.name, "projectList"],
    render: projectListRender
  }, {
    type: FieldTypes.DIVIDER
  }], [projectListRender, isConfigSsc]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={_fields}
      formDefaultValues={initialFormValues}
      newRowDefaultValue={initialFormValues[0]}
      {...props}
      onValuesChange={onValuesChange}
    />
  )
})

export default FortifyMappingForm;
